import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { acceptTos, TOS_VERSION } from '../lib/access';

// Terms & Privacy acceptance screen. Shown once after age capture (13+) or
// after parental consent (under-13) - and again whenever TOS_VERSION is bumped.
//
// Compliance: gives us a signed audit trail (tos_accepted_at + tos_version) that
// the user (or their parent) agreed to the terms before using the product.
export function TermsAcceptanceScreen() {
  const { user, refreshProfile } = useAuth();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAccept() {
    if (!user) {
      setError('You need to be signed in.');
      return;
    }
    if (!checked) {
      setError('Please tick the box to confirm you accept the Terms and Privacy Policy.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await acceptTos(user.id);
      if (!saved) throw new Error('Could not save your acceptance. Please try again.');
      await refreshProfile();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell title="Terms &amp; Privacy">
      <div className="px-5 pt-6 pb-8 max-w-md mx-auto">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">One more thing</h1>
        <p className="mt-3 text-sm text-ink/70 leading-relaxed">
          Before we get started, please take a moment to read our Terms of Service and Privacy Policy. They
          explain what My Dyslexia Tools does with your worksheet photos, what data we keep, and what you can
          ask us to delete.
        </p>

        <div className="mt-6 rounded-2xl bg-white border border-ink/10 p-5">
          <p className="text-sm font-semibold text-ink mb-3">Quick summary</p>
          <ul className="text-sm text-ink/75 space-y-2 list-disc pl-5">
            <li>Your worksheet photos are processed by AI to detect the questions, then discarded. We do not keep the images.</li>
            <li>Your answers stay on your device by default. We only sync them to our servers if you turn cloud sync on.</li>
            <li>We don&apos;t train AI models on your data. Ever.</li>
            <li>You can delete your account and everything we hold from Settings at any time.</li>
          </ul>
          <p className="mt-4 text-sm text-ink/70">
            Full detail:{' '}
            <Link className="underline" to="/terms" target="_blank" rel="noreferrer">Terms of Service</Link>{' '}
            &middot;{' '}
            <Link className="underline" to="/privacy" target="_blank" rel="noreferrer">Privacy Policy</Link>{' '}
            &middot;{' '}
            <Link className="underline" to="/ai-policy" target="_blank" rel="noreferrer">AI Policy</Link>
          </p>
        </div>

        <label className="flex items-start gap-3 mt-6 cursor-pointer">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-1 h-5 w-5 rounded border-ink/25 text-accent focus:ring-accent"
          />
          <span className="text-sm text-ink leading-relaxed">
            I have read and accept the <Link to="/terms" className="underline" target="_blank" rel="noreferrer">Terms of Service</Link>, the{' '}
            <Link to="/privacy" className="underline" target="_blank" rel="noreferrer">Privacy Policy</Link>, and the{' '}
            <Link to="/ai-policy" className="underline" target="_blank" rel="noreferrer">AI Policy</Link>.
          </span>
        </label>

        {error && (
          <div role="alert" className="mt-4 rounded-xl bg-warm border border-amber-300 text-ink px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleAccept}
          disabled={busy || !checked}
          className="w-full mt-6 inline-flex items-center justify-center bg-accent text-white px-6 py-3 rounded-full font-medium hover:brightness-105 transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {busy ? 'Saving...' : 'I accept - continue to the app'}
        </button>

        <p className="mt-6 text-xs text-ink/45 leading-relaxed">
          Terms version {TOS_VERSION}. If we materially update the Terms, we&apos;ll ask you to accept the
          new version on your next visit.
        </p>
      </div>
    </AppShell>
  );
}
